/* Forgot Password — public, requests a reset email. */
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (e) {
      // Backend never reveals existence — but errors still possible (network etc.)
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit request. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12" data-testid="forgot-password-page">
      <div className="mx-auto max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center text-sm text-slate-600 underline-offset-2 hover:underline" data-testid="fp-back-link">
          <ArrowLeft className="mr-1 size-4" /> Back to sign in
        </Link>
        <Card className="border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><Mail className="size-5" /></div>
            <div>
              <h1 className="font-heading text-xl font-black tracking-tight text-slate-900">Forgot password?</h1>
              <p className="mt-1 text-sm text-slate-600">Enter the email you use to sign in to FlowQA. We will send a link to reset your password.</p>
            </div>
          </div>

          {done ? (
            <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="fp-done">
              <div className="font-semibold">Request received.</div>
              <p className="mt-1">If <b>{email}</b> is registered with FlowQA, a password reset link has been sent. It will expire in 60 minutes.</p>
              <p className="mt-2 text-xs">Check your inbox — and your Spam folder, just in case.</p>
              <Link to="/" className="mt-3 inline-block text-sm font-semibold text-teal-700 hover:underline">Back to sign in →</Link>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label>Email</Label>
                <Input
                  type="email" autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@your-company.com.au"
                  data-testid="fp-email-input"
                  required
                />
              </div>
              {err && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="fp-error">{err}</div>
              )}
              <Button type="submit" disabled={submitting || !email} className="w-full bg-teal-700 hover:bg-teal-800" data-testid="fp-submit-btn">
                {submitting ? (<><Loader2 className="mr-2 size-4 animate-spin" /> Sending…</>) : "Send reset link"}
              </Button>
              <p className="text-xs text-slate-500">For security, we will respond with the same message whether the email is registered or not.</p>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
